import React, { useContext, useState, useRef, useEffect } from "react";
import { LuBriefcase, LuUpload } from "react-icons/lu";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone } from "../controller/global";
import useToast from "./useToast";
import useDialog from "./useDialog";

const JobApply = () => {
    const { isJobFormOpen, setIsJobFormOpen, jobFormData, setJobFormData } = useContext(GlobalStateContext);
    const [errors, setErrors] = useState({});
    const [messageApi, contextHolder] = useToast();
    const formRef = useRef(); // Create a ref for the form
    const dialogRef = useDialog(isJobFormOpen, () => setIsJobFormOpen(false));

    // Lock background scroll while the modal is open so only the modal's
    // own content scrolls, not the page behind it.
    useEffect(() => {
        if (isJobFormOpen) {
            const previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = previousOverflow; };
        }
    }, [isJobFormOpen]);

    const SERVICE_ID = "service_kdqpdc7";  // Replace with your service ID from EmailJS
    const TEMPLATE_ID = "template_9ah0vyi";  // Replace with your template ID from EmailJS
    const USER_ID = "4zj85YCmOxjtxhGNN";  // Replace with your user ID from EmailJS

    const validateField = (name, value) => {
        const trimmed = (value || "").toString().trim();
        let error = "";
        switch (name) {
            case "fullname":
                if (!trimmed) error = "Full name is required.";
                break;
            case "phone":
                if (!trimmed) error = "Phone number is required.";
                else if (!isValidPhone(trimmed)) error = "Enter a valid phone number.";
                break;
            case "email":
                if (!trimmed) error = "Email is required.";
                else if (!emailRegex.test(trimmed.toLowerCase())) error = "Enter a valid email address.";
                break;
            case "role":
                if (!trimmed) error = "Role is required.";
                break;
            case "experience":
                if (!trimmed) error = "Year of experience is required.";
                break;
            case "about":
                if (!trimmed) error = "Tell us about yourself.";
                break;
            case "resume":
                if (!value) error = "Resume is required.";
                break;
            default:
                break;
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        const fieldValue = name === "resume" ? files[0] : value;
        setJobFormData((prev) => ({ ...prev, [name]: fieldValue }));
        const error = validateField(name, fieldValue);
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const success = () => {
        messageApi.open({
            type: "success",
            content: "Thanks for Applying!",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {
            fullname: validateField("fullname", jobFormData.fullname),
            phone: validateField("phone", jobFormData.phone),
            email: validateField("email", jobFormData.email),
            role: validateField("role", jobFormData.role),
            experience: validateField("experience", jobFormData.experience),
            about: validateField("about", jobFormData.about),
            resume: validateField("resume", jobFormData.resume),
        };

        if (Object.values(newErrors).some((error) => error)) {
            setErrors(newErrors);
            return;
        }

        // Use formRef to get the form element for EmailJS
        const form = formRef.current;

        try {
            // Loaded on demand so the EmailJS SDK is not part of every page
            // load — this component is mounted by Layout on every route, but
            // the SDK is only needed once somebody actually applies.
            const { default: emailjs } = await import("emailjs-com");
            const response = await emailjs.sendForm(
                SERVICE_ID,
                TEMPLATE_ID,
                form,
                USER_ID
            );

            if (response.status === 200) {
                success();
                handleClose();
            } else {
                messageApi.open({
                    type: "error",
                    content: "There was an error sending your application.",
                });
            }
        } catch (error) {
            messageApi.open({
                type: "error",
                content: "There was an error with the submission. Please try again later.",
            });
        }
    };

    const handleClose = () => {
        setJobFormData({
            fullname: "",
            phone: "",
            email: "",
            role: "",
            experience: "",
            about: "",
            resume: "",
        });
        setErrors({});
        setIsJobFormOpen(false);
    };

    // Labeled field, matching the .ea-field pattern used by the site's other
    // contact forms — reuses global CSS custom properties (--pine, --leaf,
    // etc.) declared at :root, so it looks consistent wherever this modal
    // is opened from, not just the editorial-ai pages.
    // WCAG 1.3.5 Identify Input Purpose.
    const AUTOCOMPLETE = { fullname: "name", phone: "tel", email: "email" };

    const field = (label, name, type = "text") => (
        <div className="ea-field">
            <label htmlFor={`jobapply-${name}`}>{label}</label>
            <input
                id={`jobapply-${name}`}
                type={type}
                name={name}
                autoComplete={AUTOCOMPLETE[name] || "off"}
                value={jobFormData[name] || ""}
                onChange={handleChange}
                aria-invalid={Boolean(errors[name])}
                aria-describedby={errors[name] ? `jobapply-${name}-error` : undefined}
            />
            {errors[name] && <p id={`jobapply-${name}-error`} className="ea-field-error">{errors[name]}</p>}
        </div>
    );

    return (
        <>
            {contextHolder}
            {isJobFormOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
                    <div className="absolute inset-0 bg-black opacity-60 z-10" onClick={handleClose}></div>

                    <div
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="jobapply-title"
                        className="relative z-20 rounded-lg w-full max-w-md sm:max-w-lg lg:max-w-xl max-h-screen overflow-y-auto no-scrollbar"
                        style={{ background: "var(--paper)", border: "1px solid var(--line)", boxShadow: "10px 10px 0 var(--leaf)" }}
                    >
                        <div style={{ height: 4, background: "var(--leaf)" }} />
                        <div
                            className="flex justify-between items-start px-6 py-5 sticky top-0 z-10"
                            style={{ background: "var(--paper)", borderBottom: "1px solid var(--line)", gap: 16 }}
                        >
                            <div className="flex items-start" style={{ gap: 14 }}>
                                <div className="ab-tile-icon" style={{ marginBottom: 0, flexShrink: 0 }}>
                                    <LuBriefcase aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 id="jobapply-title" style={{ fontFamily: "Syne, sans-serif", fontSize: "var(--t-lg)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-.03em", margin: 0 }}>
                                        Job Application
                                    </h3>
                                    <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: "var(--t-2xs)" }}>
                                        We'll get back to you within one business day.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                aria-label="Close"
                                className="w-8 h-8 flex justify-center items-center rounded-lg cursor-pointer transition-colors duration-200"
                                style={{ color: "var(--muted)", flexShrink: 0 }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--leaf)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--muted)"; }}
                            >
                                <span className="text-xl">×</span>
                            </button>
                        </div>

                        <form ref={formRef} onSubmit={handleSubmit} className="ea-form px-6 py-6" style={{ marginTop: 0 }} noValidate>
                            {field("Full Name", "fullname")}
                            <div className="ea-form-row">
                                {field("Phone Number", "phone", "tel")}
                                {field("Email", "email", "email")}
                            </div>
                            <div className="ea-form-row">
                                {field("Role", "role")}
                                {field("Year of Experience", "experience", "number")}
                            </div>

                            <div className="ea-field">
                                <label>Resume</label>
                                <div className="ea-file-upload">
                                    <input
                                        id="jobapply-resume"
                                        className="ea-file-input"
                                        type="file"
                                        name="resume"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleChange}
                                        aria-invalid={Boolean(errors.resume)}
                                        aria-describedby={errors.resume ? "jobapply-resume-error" : undefined}
                                    />
                                    <label htmlFor="jobapply-resume" className="ea-file-button">
                                        <LuUpload aria-hidden="true" /> Choose file
                                    </label>
                                    <span className="ea-file-name">
                                        {jobFormData.resume ? jobFormData.resume.name : "PDF, DOC, or DOCX"}
                                    </span>
                                </div>
                                {errors.resume && <p id="jobapply-resume-error" className="ea-field-error">{errors.resume}</p>}
                            </div>

                            <div className="ea-field">
                                <label htmlFor="jobapply-about">Tell us about yourself</label>
                                <textarea
                                    id="jobapply-about"
                                    name="about"
                                    rows={4}
                                    value={jobFormData.about || ""}
                                    onChange={handleChange}
                                    aria-invalid={Boolean(errors.about)}
                                    aria-describedby={errors.about ? "jobapply-about-error" : undefined}
                                />
                                {errors.about && <p id="jobapply-about-error" className="ea-field-error">{errors.about}</p>}
                            </div>

                            <div className="flex items-center justify-end gap-5 pt-2" style={{ borderTop: "1px solid var(--line)", marginTop: 4, paddingTop: 18 }}>
                                <button type="button" onClick={handleClose} className="ea-text-link">
                                    Cancel
                                </button>
                                <button type="submit" className="ea-button ea-primary">
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default JobApply;
